import { API_BASE } from "./api";
import { sanitizeChatContext } from "../chatContextDrag";

const buildAuthHeaders = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isTransientFetchError = (error) => {
  const message = String(error?.message || error || "");
  return /failed to fetch|networkerror|network request failed|load failed|network changed/i.test(
    message
  );
};

const fetchWithRetry = async (url, options, { retries = 2, retryDelayMs = 350 } = {}) => {
  let attempt = 0;
  let lastError = null;

  while (attempt <= retries) {
    try {
      return await fetch(url, options);
    } catch (error) {
      lastError = error;
      if (attempt >= retries || !isTransientFetchError(error)) {
        throw error;
      }
      await wait(retryDelayMs * (attempt + 1));
      attempt += 1;
    }
  }

  throw lastError || new Error("Request failed");
};

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

const normalizeChatContextIdentity = (chatContext = null) => {
  const normalized = sanitizeChatContext(chatContext);
  if (!normalized) return "main";
  const reportIds = Array.isArray(normalized.reportIds)
    ? normalized.reportIds.map(normalizeText).filter(Boolean)
    : [];
  if (reportIds.length) return `reports:${reportIds.join(",")}`;
  const collectionName = normalizeText(normalized.collectionName);
  const key = normalizeText(normalized.activeKey || normalized.key);
  if (collectionName && key) return `collection:${collectionName}:key:${key}`;
  if (key) return `key:${key}`;
  const label = normalizeText(normalized.label).toLowerCase();
  return label ? `label:${label}` : "main";
};

function fallbackSessionTitle(session = {}) {
  const preview = normalizeText(session?.preview);
  if (preview) return preview;
  return "New conversation";
}

function normalizeSessionSummary(session = {}) {
  return {
    sessionId: String(session?.sessionId || ""),
    channelId: session?.channelId || session?.channel_id || null,
    subchannelId: session?.subchannelId || session?.subchannel_id || null,
    originChannelId:
      session?.originChannelId || session?.origin_channel_id || null,
    title: normalizeText(session?.title) || fallbackSessionTitle(session),
    preview: normalizeText(session?.preview),
    currentContext: session?.currentContext || session?.current_context || null,
    contextTrail: Array.isArray(session?.contextTrail) ? session.contextTrail : [],
    detachedContextKeys: Array.isArray(session?.detachedContextKeys) ? session.detachedContextKeys : [],
    messageCount: Number(session?.messageCount || 0),
    startedAt: session?.startedAt || null,
    lastActivityAt: session?.lastActivityAt || null,
    lastMessageAt: session?.lastMessageAt || session?.lastActivityAt || null,
  };
}

export function createChannelChatSessionId(userId, channelId) {
  const baseUserId = String(userId || "").trim();
  const baseChannelId = String(channelId || "").trim();
  const timestamp = new Date()
    .toISOString()
    .replace(/[-:.TZ]/g, "")
    .slice(0, 14);
  const random = Math.random().toString(36).slice(2, 8);
  return `channel_${baseUserId}_${baseChannelId}_${timestamp}_${random}`;
}

export function getChannelChatContextIdentity(chatContext = null) {
  return normalizeChatContextIdentity(chatContext);
}

export async function sendChannelChatMessage({
  userId,
  channelId,
  message,
  sessionId,
  chatContext,
  authToken,
}) {
  const outgoingChatContext = sanitizeChatContext(chatContext);
  const res = await fetch(`${API_BASE}/channel-chat/messages`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({
      user_id: userId,
      channelId,
      sessionId,
      message,
      chatContext: outgoingChatContext || null,
    }),
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(`ChannelChat API error ${res.status}: ${text}`);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse /channel-chat/messages JSON: "${text}"`);
  }
}

export async function getChannelChatContextOptions({
  userId,
  shopId,
  scope,
  reportId,
  summaryId,
  authToken,
}) {
  const url = new URL(`${API_BASE}/channel-chat/context-options`);
  url.searchParams.set("userId", userId);
  url.searchParams.set("shopId", shopId);
  if (scope) {
    url.searchParams.set("scope", scope);
  }
  if (reportId) {
    url.searchParams.set("reportId", reportId);
  }
  if (summaryId) {
    url.searchParams.set("summaryId", summaryId);
  }

  const res = await fetchWithRetry(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(`ChannelChat Context Options API error ${res.status}: ${text}`);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse /channel-chat/context-options JSON: "${text}"`);
  }
}

export async function getChannelChatHistory({
  sessionId,
  userId,
  channelId,
  authToken,
}) {
  const url = new URL(`${API_BASE}/channel-chat/history`);
  url.searchParams.set("sessionId", sessionId);
  url.searchParams.set("userId", userId);
  if (channelId) {
    url.searchParams.set("channelId", channelId);
  }

  const res = await fetchWithRetry(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(`ChannelChat History API error ${res.status}: ${text}`);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse /channel-chat/history JSON: "${text}"`);
  }
}

export async function getChannelChatSessions({
  userId,
  channelId,
  scope,
  limit,
  skip,
  authToken,
}) {
  const url = new URL(`${API_BASE}/channel-chat/sessions`);
  url.searchParams.set("userId", userId);
  if (channelId) {
    url.searchParams.set("channelId", channelId);
  }
  if (scope) {
    url.searchParams.set("scope", scope);
  }
  if (limit) {
    url.searchParams.set("limit", String(limit));
  }
  if (skip) {
    url.searchParams.set("skip", String(skip));
  }

  const res = await fetchWithRetry(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(`ChannelChat Sessions API error ${res.status}: ${text}`);
  }

  try {
    const parsed = JSON.parse(text);
    return {
      ...parsed,
      sessions: Array.isArray(parsed?.sessions)
        ? parsed.sessions.map(normalizeSessionSummary).filter((session) => session.sessionId)
        : [],
    };
  } catch {
    throw new Error(`Failed to parse /channel-chat/sessions JSON: "${text}"`);
  }
}

export async function getLatestChannelChatSession({
  userId,
  channelId,
  chatContext,
  authToken,
}) {
  const url = new URL(`${API_BASE}/channel-chat/sessions/latest`);
  url.searchParams.set("userId", userId);
  url.searchParams.set("channelId", channelId);
  if (chatContext?.key) {
    url.searchParams.set("contextKey", String(chatContext.key));
  }
  if (chatContext?.type) {
    url.searchParams.set("contextType", String(chatContext.type));
  }
  if (chatContext?.collectionName) {
    url.searchParams.set("collectionName", String(chatContext.collectionName));
  }
  if (chatContext?.reportId) {
    url.searchParams.set("reportId", String(chatContext.reportId));
  }
  if (chatContext?.label) {
    url.searchParams.set("label", String(chatContext.label));
  }

  const res = await fetchWithRetry(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(`ChannelChat Latest Session API error ${res.status}: ${text}`);
  }

  try {
    const parsed = JSON.parse(text);
    return {
      ...parsed,
      session: parsed?.session ? normalizeSessionSummary(parsed.session) : null,
    };
  } catch {
    throw new Error(`Failed to parse /channel-chat/sessions/latest JSON: "${text}"`);
  }
}

export async function attachChannelChatContext({
  userId,
  sessionId,
  channelId,
  context,
  authToken,
}) {
  const res = await fetchWithRetry(`${API_BASE}/channel-chat/context/attach`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({ userId, sessionId, channelId, context }),
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(`ChannelChat attach context error ${res.status}: ${text}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse /channel-chat/context/attach JSON: "${text}"`);
  }
}

export async function removeChannelChatContext({
  userId,
  sessionId,
  contextKey,
  authToken,
}) {
  const res = await fetchWithRetry(`${API_BASE}/channel-chat/context/remove`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({ userId, sessionId, contextKey }),
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(`ChannelChat remove context error ${res.status}: ${text}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse /channel-chat/context/remove JSON: "${text}"`);
  }
}

export async function refreshProductChatContext({
  userId,
  sessionId,
  contextKey,
  productUpdates,
  authToken,
}) {
  const res = await fetchWithRetry(`${API_BASE}/channel-chat/context/refresh-product`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({ userId, sessionId, contextKey, productUpdates }),
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(`ChannelChat refresh product context error ${res.status}: ${text}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse /channel-chat/context/refresh-product JSON: "${text}"`);
  }
}
